import React, { useCallback } from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Select from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import SelectField from "@app/component/input/SelectField";
import NotificationSettingsDealClosingInterval, {
  getNotificationSettingsDealClosingIntervalDisplayName,
} from "@app/enum/NotificationSettingsDealClosingInterval";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";

import styles from "../../NotificationsCommon.module.scss";

interface Props {
  enabledNamePath: string[];
  form: FormInstance<NotificationSettingValue>;
  level: "owned" | "shared";
}

const DealClosing: React.FC<Props> = ({ enabledNamePath, form, level }) => {
  const intl = useIntl();
  const handleChange = useCallback(
    (enabled: boolean, interval: NotificationSettingsDealClosingInterval) => {
      form.setFieldsValue({
        recordUpdates: { [level]: { dealsClosing: { enabled, interval } } },
      });
    },
    [form, level]
  );

  return (
    <div>
      <Form.Item
        className={cn(styles.formItem, styles.dealClosingContainer)}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].dealsClosing.enabled !==
            next.recordUpdates[level].dealsClosing.enabled
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "dealsClosing", "enabled"])}
            className={cn(styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={(enabled) =>
              handleChange(
                enabled,
                getFieldValue(["recordUpdates", level, "dealsClosing", "interval"])
              )
            }
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.dealsClosing",
              defaultMessage: "Deals when its closing date is approaching",
              description:
                "Settings notifications Record Updates - records you own - deals closing",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={cn(styles.formItem, styles.dealClosingContainer)}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].dealsClosing.enabled !==
            next.recordUpdates[level].dealsClosing.enabled ||
          prev.recordUpdates[level].dealsClosing.interval !==
            next.recordUpdates[level].dealsClosing.interval
        }
      >
        {({ getFieldValue }) =>
          getFieldValue(["recordUpdates", level, "dealsClosing", "enabled"]) ? (
            <Form.Item name={["recordUpdates", level, "dealsClosing", "interval"]}>
              <SelectField<NotificationSettingsDealClosingInterval>
                className={styles.selectBox}
                onChange={(interval) =>
                  handleChange(
                    getFieldValue(["recordUpdates", level, "dealsClosing", "enabled"]),
                    interval
                  )
                }
              >
                {[
                  NotificationSettingsDealClosingInterval.MONTH,
                  NotificationSettingsDealClosingInterval.WEEK,
                  NotificationSettingsDealClosingInterval.DAY,
                ].map((interval) => (
                  <Select.Option key={interval} value={interval}>
                    {getNotificationSettingsDealClosingIntervalDisplayName(intl, interval)}
                  </Select.Option>
                ))}
              </SelectField>
            </Form.Item>
          ) : null
        }
      </Form.Item>
    </div>
  );
};

export default DealClosing;
